import React, { useState, useRef, useEffect } from "react";
import { ChevronDown } from "lucide-react";
import IconSvg from "../ui/IconSvg";

type SortValue = {
    name: string;
    value: string;
};

type SortOption = {
    label: string;
    values: SortValue[];
};

type SelectedSort = {
    option: string;
    value: string;
} | null;

interface SortDropdownProps {
    options: SortOption[];
    onChange?: (selected: { option: string; value: string } | null) => void;
}

const SortDropdown: React.FC<SortDropdownProps> = ({ options = [], onChange }) => {
    const [open, setOpen] = useState<boolean>(false);
    const [selected, setSelected] = useState<SelectedSort>(null);
    const dropdownRef = useRef<HTMLDivElement>(null);

    // Close dropdown on outside click
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    const handleSelect = (option: string, value: string) => {
        const newSelection = { option, value };
        setSelected(newSelection);
        setOpen(false);
        if (onChange) onChange(newSelection);
    };

    const handleReset = () => {
        setSelected(null);
        if (onChange) onChange(null);
    };

    return (
        <div className="relative inline-block text-left" ref={dropdownRef}>
            {/* Button */}
            <button
                onClick={() => setOpen(!open)}
                className="flex items-center gap-2 rounded-lg border bg-gray-200 border-gray-300 px-4 py-2 text-sm font-medium shadow-sm hover:bg-gray-50"
            >
                <IconSvg icon="sort" />  Sort
            </button>

            {/* Dropdown */}
            {open && (
                <div className="absolute left-0 z-50 mt-2 w-60 rounded-lg border border-gray-200 bg-white shadow-lg">
                    {/* Reset aligned to right */}
                    <div className="flex justify-end px-3 pt-2">
                        <button
                            className="text-xs text-blue-500 hover:underline"
                            onClick={handleReset}
                        >
                            Reset all
                        </button>
                    </div>

                    <div className="p-3">
                        {options.map((opt, idx) => (
                            <div key={idx} className="mb-3 last:mb-0">
                                <p className="mb-1 text-xs font-bold text-gray-500">
                                    {opt.label}
                                </p>
                                <div className="flex flex-row flex-wrap gap-3">
                                    {opt.values.map((val, vIdx) => (
                                        <label
                                            key={vIdx}
                                            className="flex cursor-pointer items-center gap-2 text-sm text-gray-700 hover:text-black"
                                        >
                                            <input
                                                type="radio"
                                                name={opt.label}
                                                checked={
                                                    selected?.option === opt.label &&
                                                    selected?.value === val.value
                                                }
                                                onChange={() => handleSelect(opt.label, val.value)}
                                            />
                                            {val.name}
                                        </label>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

        </div>
    );
};

export default SortDropdown;
